import { ProFormGroup } from '@ant-design/pro-components'
import { useLingui } from '@lingui/react/macro'
import type { PreferredCommunicationType } from '@pkg/form/common'
import { FavoriteField } from '@pkg/form/components/FavoriteField'
import { useFieldContext, useFormContext } from '@pkg/form/createAppFormHook'
import { joinNames } from '@pkg/form/utils/form.utils'
import { useStore } from '@tanstack/react-form'
import { useCallback } from 'react'

export interface ContactGroup {
  phone?: string | null
  fax?: string | null
  email?: string | null
  mobile?: string | null
  preferredCommunication?: PreferredCommunicationType | null
}

export type ContactGroupFieldProps = {
  className?: string
  hidePreferredCommunication?: boolean
  showMobile?: boolean
  readonly?: boolean
}

export function ContactGroupField(props: ContactGroupFieldProps) {
  const { className, hidePreferredCommunication, showMobile, readonly } = props
  const field = useFieldContext<ContactGroup>()
  const form = useFormContext()

  const { t } = useLingui()

  const preferredCommunication = useStore(field.store, s => s.value?.preferredCommunication)

  const toggleFavorite = useCallback(
    (type: PreferredCommunicationType) => {
      const currentValue = field.state.value?.preferredCommunication
      const nextValue = currentValue === type ? null : type

      field.setValue(v => ({
        ...v,
        preferredCommunication: nextValue,
      }))
    },
    [field],
  )

  return (
    <ProFormGroup title={false} rowProps={{ gutter: [16, 0] }} className={className}>
      <form.AppField name={joinNames(field, 'phone')}>
        {field => (
          <field.Text
            readonly={readonly}
            label={t`Telefon`}
            colProps={{ md: 8, xs: 24 }}
            fieldProps={{
              suffix: !hidePreferredCommunication && (
                <FavoriteField type="phone" value={preferredCommunication} onChange={toggleFavorite} />
              ),
            }}
          />
        )}
      </form.AppField>
      {showMobile && (
        <form.AppField name={joinNames(field, 'mobile')}>
          {field => (
            <field.Text
              readonly={readonly}
              label={t`Mobiltelefon`}
              colProps={{ md: 8, xs: 24 }}
              fieldProps={{
                suffix: !hidePreferredCommunication && (
                  <FavoriteField type="mobile" value={preferredCommunication} onChange={toggleFavorite} />
                ),
              }}
            />
          )}
        </form.AppField>
      )}
      <form.AppField name={joinNames(field, 'fax')}>
        {field => (
          <field.Text
            readonly={readonly}
            label={t`Fax`}
            colProps={{ md: 8, xs: 24 }}
            fieldProps={{
              suffix: !hidePreferredCommunication && (
                <FavoriteField type="fax" value={preferredCommunication} onChange={toggleFavorite} />
              ),
            }}
          />
        )}
      </form.AppField>
      <form.AppField name={joinNames(field, 'email')}>
        {field => (
          <field.Text
            readonly={readonly}
            label={t`E-Mail`}
            colProps={{ md: 8, xs: 24 }}
            fieldProps={{
              suffix: !hidePreferredCommunication && (
                <FavoriteField type="email" value={preferredCommunication} onChange={toggleFavorite} />
              ),
            }}
          />
        )}
      </form.AppField>
    </ProFormGroup>
  )
}
